import { palette } from '@/modules/defines/styles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'switch'
  | 'image'
  | 'custom';

export interface FieldConfig<T = any> {
  name: keyof T;
  label: string;
  type: FieldType;
  description?: string;
  placeholder?: string;
  options?: { value: string; label: string }[]; // for select
  renderCondition?: (formData: Partial<T>) => boolean; // 조건부 렌더링
  customRender?: (
    formData: Partial<T>,
    handleChange: (name: keyof T, value: any) => void
  ) => React.ReactNode;
  width?: 'full' | 'half'; // full = 100%, half = 50%
  isNumber?: boolean; // select 필드가 number 타입일 경우
  showLinkButton?: boolean; // 링크 이동 버튼 표시 여부 (text 타입일 때만)
  readOnly?: boolean; // 읽기 전용 필드 여부
}

interface CommonSpaceDetailContainerProps<T extends { id?: number }> {
  HeaderComponent: React.ComponentType;
  queryKey: string;
  listQueryKey: string; // 리스트 쿼리 키 (수정 후 refetch용)
  queryFn: (id: number) => Promise<any>;
  updateFn: (id: number, data: Partial<T>) => Promise<any>;
  uploadImageFn?: (id: string, file: File) => Promise<any>;
  fields: FieldConfig<T>[];
  switchFieldKey?: keyof T; // 스위치 필드의 키 (예: 'publish', 'use_app')
  switchFieldLabel?: string; // 스위치 레이블 (예: '앱에서 사용')
  defaultRoute: string;
  themeColor: string;
  themeHoverColor?: string;
}

function CommonSpaceDetailContainer<T extends { id?: number }>({
  HeaderComponent,
  queryKey,
  listQueryKey,
  queryFn,
  updateFn,
  uploadImageFn,
  fields,
  switchFieldKey,
  switchFieldLabel = '앱에서 사용',
  defaultRoute,
  themeColor,
  themeHoverColor,
}: CommonSpaceDetailContainerProps<T>) {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<T>>({});
  const [initialData, setInitialData] = useState<Partial<T>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: spaceData, isLoading } = useQuery({
    queryKey: [queryKey, id],
    queryFn: () => queryFn(Number(id)),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<T>) => updateFn(Number(id), data),
    onSuccess: () => {
      alert('수정되었습니다');
      // 리스트 쿼리 무효화하여 목록 페이지에서 refetch
      queryClient.invalidateQueries({ queryKey: [listQueryKey] });
      // 현재 상세 쿼리도 무효화하여 데이터 새로고침
      queryClient.invalidateQueries({ queryKey: [queryKey, id] });
      // navigate(defaultRoute); // 페이지 이동 제거 - 현재 페이지 유지
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: (file: File) => {
      if (!uploadImageFn) {
        throw new Error('Image upload function not provided');
      }
      return uploadImageFn(id!, file);
    },
    onSuccess: (response) => {
      alert('이미지가 업로드되었습니다.');
      window.location.reload();
    },
    onError: (error) => {
      console.error('Image upload error:', error);
      alert('✗ 이미지 업로드에 실패했습니다. 다시 시도해주세요.');
    },
  });

  useEffect(() => {
    if (spaceData?.data?.data) {
      setFormData(spaceData.data.data);
      setInitialData(spaceData.data.data);
    }
  }, [spaceData]);

  const handleChange = (name: keyof T, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleSwitch = () => {
    if (switchFieldKey) {
      setFormData((prev) => ({
        ...prev,
        [switchFieldKey]: !prev[switchFieldKey],
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const isFormChanged = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialData);
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadImageMutation.mutate(e.target.files[0]);
    }
  };

  const renderField = (field: FieldConfig<T>) => {
    // 조건부 렌더링 체크
    if (field.renderCondition && !field.renderCondition(formData)) {
      return null;
    }

    const value = formData[field.name];

    switch (field.type) {
      case 'text':
      case 'number':
        return (
          <FormGroup key={String(field.name)}>
            <Label>{field.label}</Label>
            <InputWithButton>
              <Input
                type={field.type}
                name={String(field.name)}
                value={(value as any) || ''}
                onChange={(e) =>
                  handleChange(
                    field.name,
                    field.type === 'number'
                      ? Number(e.target.value)
                      : e.target.value
                  )
                }
                placeholder={field.placeholder}
                $hasButton={field.showLinkButton}
                readOnly={field.readOnly}
              />
              {field.showLinkButton && value && (
                <LinkButton
                  type="button"
                  onClick={() => window.open(value as string, '_blank')}
                  disabled={!value}
                >
                  이동
                </LinkButton>
              )}
            </InputWithButton>
            {field.description && (
              <Description>{field.description}</Description>
            )}
          </FormGroup>
        );

      case 'textarea':
        return (
          <FormGroup key={String(field.name)}>
            <Label>{field.label}</Label>
            <TextArea
              name={String(field.name)}
              value={(value as any) || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder={field.placeholder}
            />
            {field.description && (
              <Description>{field.description}</Description>
            )}
          </FormGroup>
        );

      case 'select':
        return (
          <FormGroup key={String(field.name)}>
            <Label>{field.label}</Label>
            <Select
              name={String(field.name)}
              value={String(value || '')}
              onChange={(e) =>
                handleChange(
                  field.name,
                  field.isNumber ? Number(e.target.value) : e.target.value
                )
              }
            >
              <option value="">선택하세요</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {field.description && (
              <Description>{field.description}</Description>
            )}
          </FormGroup>
        );

      case 'image':
        return (
          <FormGroup key={String(field.name)}>
            <Label>{field.label}</Label>
            <ImageContainer>
              {value ? (
                <ImagePreviewWrapper>
                  <ImagePreview src={value as string} alt={field.label} />
                  {uploadImageMutation.isPending ? (
                    <ImageLoadingOverlay>
                      <LoadingSpinner />
                      <LoadingText>업로드 중...</LoadingText>
                    </ImageLoadingOverlay>
                  ) : (
                    <ImageHoverOverlay>
                      <ImageActionButtons>
                        <ImageActionButton
                          type="button"
                          onClick={handleImageUploadClick}
                          title="이미지 업로드"
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                        </ImageActionButton>
                        <ImageActionButton
                          type="button"
                          onClick={() => {
                            window.open(value as string, '_blank');
                          }}
                          title="이미지 다운로드"
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </ImageActionButton>
                      </ImageActionButtons>
                    </ImageHoverOverlay>
                  )}
                </ImagePreviewWrapper>
              ) : (
                <ImagePlaceholder
                  onClick={handleImageUploadClick}
                  style={{
                    opacity: uploadImageMutation.isPending ? 0.6 : 1,
                    pointerEvents: uploadImageMutation.isPending
                      ? 'none'
                      : 'auto',
                  }}
                >
                  {uploadImageMutation.isPending ? (
                    <>
                      <LoadingSpinner />
                      <PlaceholderText style={{ marginTop: '12px' }}>
                        업로드 중...
                      </PlaceholderText>
                    </>
                  ) : (
                    <PlaceholderText>클릭하여 이미지 업로드</PlaceholderText>
                  )}
                </ImagePlaceholder>
              )}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleImageChange}
              />
            </ImageContainer>
            {field.description && (
              <Description>{field.description}</Description>
            )}
          </FormGroup>
        );

      case 'custom':
        return field.customRender?.(formData, handleChange) || null;

      default:
        return null;
    }
  };

  if (isLoading) {
    return <LoadingMessage>데이터를 불러오는 중입니다...</LoadingMessage>;
  }

  // 필드를 full width와 half width로 그룹화
  const fullWidthFields = fields.filter((f) => !f.width || f.width === 'full');
  const halfWidthFields = fields.filter((f) => f.width === 'half');

  // half width 필드를 2개씩 묶기
  const halfWidthPairs: FieldConfig<T>[][] = [];
  for (let i = 0; i < halfWidthFields.length; i += 2) {
    halfWidthPairs.push(halfWidthFields.slice(i, i + 2));
  }

  return (
    <Container>
      <HeaderComponent />
      <Content>
        <ContentWrapper>
          <TabContainer>
            <TabButtons>
              <TabButton $active={true} $themeColor={themeColor}>
                정보
              </TabButton>
            </TabButtons>
            <SaveButton
              type="button"
              onClick={handleSubmit}
              disabled={!isFormChanged()}
              $themeColor={themeColor}
              $hoverColor={themeHoverColor}
            >
              저장
            </SaveButton>
          </TabContainer>

          <Form onSubmit={handleSubmit}>
            {/* 스위치 필드 */}
            {switchFieldKey && (
              <FormGroup>
                <SwitchLabel>
                  <Label>{switchFieldLabel}</Label>
                  <SwitchWrapper onClick={handleToggleSwitch}>
                    <SwitchInput
                      type="checkbox"
                      checked={(formData[switchFieldKey] as boolean) || false}
                      onChange={handleToggleSwitch}
                    />
                    <SwitchSlider
                      className={formData[switchFieldKey] ? 'checked' : ''}
                      $themeColor={themeColor}
                    />
                    <SwitchStatus>
                      {formData[switchFieldKey] ? 'ON' : 'OFF'}
                    </SwitchStatus>
                  </SwitchWrapper>
                </SwitchLabel>
                <Description>
                  앱에서 해당 공간을 표시할지 여부를 설정합니다
                </Description>
              </FormGroup>
            )}

            {/* Full width 필드들 */}
            {fullWidthFields.map((field) => renderField(field))}

            {/* Half width 필드들 (2개씩 묶어서) */}
            {halfWidthPairs.map((pair, index) => (
              <TwoColumnLayout key={`pair-${index}`}>
                {pair.map((field) => renderField(field))}
              </TwoColumnLayout>
            ))}
          </Form>
        </ContentWrapper>
      </Content>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

const Content = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: #f5f5f5;
  overflow-y: scroll;
  height: calc(100vh - 120px);
  position: relative;
`;

const ContentWrapper = styled.div`
  overflow-y: scroll;
  height: calc(100vh - 120px);
  position: relative;
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  margin: 0 auto;
`;

const Form = styled.form`
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #1e293b;
`;

const Input = styled.input<{ $hasButton?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  padding-right: ${({ $hasButton }) => ($hasButton ? '90px' : '0.75rem')};
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${palette.main900};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const InputWithButton = styled.div`
  position: relative;
  width: 100%;
`;

const LinkButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 6px 16px;
  background-color: ${palette.systemBlue};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #0066b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;

  &:focus {
    outline: none;
    border-color: ${palette.main900};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const Description = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.25rem;
  margin-bottom: 0;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${palette.main900};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  margin-top: 0.5rem;
`;

const ImagePreviewWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  overflow: hidden;

  &:hover > div {
    opacity: 1;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
`;

const ImageHoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 8px;
`;

const ImageLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 1;
  border-radius: 8px;
  z-index: 10;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: white;
  font-size: 0.9rem;
  margin-top: 12px;
  font-weight: 500;
`;

const ImageActionButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const ImageActionButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  color: #1e293b;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: white;
    transform: scale(1.1);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  max-width: 300px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f1f5f9;
  }
`;

const PlaceholderText = styled.span`
  color: #94a3b8;
  font-size: 0.9rem;
`;

const LoadingMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: #666;
`;

const SwitchLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SwitchWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const SwitchInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;
  position: absolute;
`;

const SwitchSlider = styled.span<{ $themeColor: string }>`
  cursor: pointer;
  width: 50px;
  height: 26px;
  background-color: #ccc;
  display: inline-block;
  border-radius: 26px;
  position: relative;
  transition: 0.4s;

  &:before {
    position: absolute;
    content: '';
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    border-radius: 50%;
    transition: 0.4s;
  }

  &.checked {
    background-color: ${(props) => props.$themeColor};
  }

  &.checked:before {
    transform: translateX(24px);
  }
`;

const SwitchStatus = styled.span`
  margin-left: 10px;
  font-weight: 600;
  color: #1e293b;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 2rem;
`;

const TabButtons = styled.div`
  display: flex;
`;

const TabButton = styled.button<{ $active: boolean; $themeColor: string }>`
  padding: 0.75rem 1.5rem;
  background-color: ${(props) =>
    props.$active ? props.$themeColor : 'transparent'};
  color: ${(props) => (props.$active ? 'white' : '#1e293b')};
  border: none;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  font-size: 1rem;
  margin-right: 0.5rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) =>
      props.$active ? props.$themeColor : '#f1f5f9'};
  }
`;

const SaveButton = styled.button<{
  $themeColor: string;
  $hoverColor?: string;
}>`
  padding: 0.5rem 1.5rem;
  background-color: ${(props) => props.$themeColor};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background-color: ${(props) => props.$hoverColor || props.$themeColor};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

export default CommonSpaceDetailContainer;
